/**
 * EmbedTruckWeek
 *
 * Standalone, iframe-friendly page that food truck owners paste into
 * their own website. Renders this truck's upcoming week as a Leaflet
 * map + scrollable event list, with every interaction routing back to
 * thetrucktrackr.com (new tab) so the embed is a discovery surface,
 * not a black hole.
 *
 * Route: /embed/truck/:slug/week
 * Data:  GET /api/embed/truck/:slug/week (gated to Pro owners
 *        server-side; non-Pro returns { ok: false, gated: "pro" }).
 *
 * Design constraints:
 *   - No global navbar, no auth, no chrome — must blend into any host site.
 *   - Light theme only (handles 95% of host sites cleanly).
 *   - "Powered by TruckTrackr →" footer is mandatory — this is the SEO
 *     backlink that justifies the feature existing.
 *   - All click-throughs open in a new tab so we never trap the host
 *     site's visitor inside an iframe.
 *   - Mobile: map on top, list below.  Desktop: side by side.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "../lib/api";
import { fmtET, etDateKey, etDateKeyFromMs } from "../lib/etTime";

const TT_BASE = "https://thetrucktrackr.com";

const _pinIcon = new L.DivIcon({
  className: "tt-embed-pin",
  iconSize: [28, 36],
  iconAnchor: [14, 34],
  popupAnchor: [0, -34],
  html: `<svg viewBox="0 0 28 36" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 0C6.27 0 0 6.27 0 14c0 9.5 13 21 13.4 21.5.3.3.9.3 1.2 0C15 35 28 23.5 28 14 28 6.27 21.73 0 14 0z" fill="#ea580c"/>
    <circle cx="14" cy="14" r="5" fill="#fff"/>
  </svg>`,
});

function _fmtTime(iso) {
  if (!iso) return "";
  return fmtET(iso, { hour: "numeric", minute: "2-digit" }).replace(":00", "");
}

function _fmtDay(iso) {
  if (!iso) return "";
  const key = etDateKey(iso);
  const nowMs = Date.now();
  if (key === etDateKeyFromMs(nowMs)) return "Today";
  if (key === etDateKeyFromMs(nowMs, 1)) return "Tomorrow";
  return fmtET(iso, { weekday: "short", month: "short", day: "numeric" });
}

function _linkForEvent(ev) {
  // Send clicks back to the main map view, pre-selecting this event's
  // day + truck. utm tags so we can see embed-driven traffic separately
  // in any future analytics build.
  return (
    `${TT_BASE}/map?truck=${encodeURIComponent(ev.truck_id || ev.id || "")}` +
    `&utm_source=embed&utm_medium=schedule_widget&utm_campaign=truck_embed`
  );
}

export default function EmbedTruckWeek() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, data: null, err: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(
          `/embed/truck/${encodeURIComponent(slug)}/week`
        );
        if (!cancelled) setState({ loading: false, data, err: null });
      } catch (e) {
        if (!cancelled) {
          setState({
            loading: false,
            data: null,
            err: e?.response?.status === 404 ? "not_found" : "error",
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const sortedEvents = useMemo(() => {
    const items = state.data?.events || [];
    return [...items].sort((a, b) => {
      const sa = Date.parse(a.starts_at || 0);
      const sb = Date.parse(b.starts_at || 0);
      return sa - sb;
    });
  }, [state.data]);

  const center = useMemo(() => {
    if (sortedEvents.length === 0) return [40.04, -76.30]; // Lancaster PA fallback
    let lat = 0;
    let lng = 0;
    for (const e of sortedEvents) {
      lat += e.lat;
      lng += e.lng;
    }
    return [lat / sortedEvents.length, lng / sortedEvents.length];
  }, [sortedEvents]);

  // --- Render states -------------------------------------------------------

  if (state.loading) {
    return (
      <Frame>
        <div className="p-6 text-center text-sm text-gray-500">Loading…</div>
      </Frame>
    );
  }
  if (state.err === "not_found" || !state.data) {
    return (
      <Frame>
        <div className="p-6 text-center text-sm text-gray-600">
          This schedule isn&apos;t available right now.
        </div>
      </Frame>
    );
  }
  if (state.data.ok === false && state.data.gated === "pro") {
    // Non-Pro stub: looks intentional, not broken. Soft upsell.
    return (
      <Frame>
        <div className="p-6 sm:p-8 text-center">
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-orange-600">
            {state.data.truck_name}
          </div>
          <div className="mt-2 font-bold text-lg text-gray-900">
            Live schedule coming soon
          </div>
          <p className="mt-2 text-sm text-gray-600 max-w-sm mx-auto">
            This truck&apos;s embedded schedule is a TruckTrackr Pro feature.
          </p>
          <a
            href={`${TT_BASE}/upgrade?utm_source=embed&utm_medium=schedule_widget&utm_campaign=embed_gated`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-5 inline-block rounded-full bg-orange-600 hover:bg-orange-700 text-white font-semibold text-sm px-5 py-2 transition"
            data-testid="embed-upgrade-cta"
          >
            Learn about Pro →
          </a>
        </div>
      </Frame>
    );
  }

  return (
    <Frame>
      <div className="px-4 pt-4 pb-2 border-b border-gray-100">
        <div className="font-bold text-base text-gray-900 truncate">
          {state.data.truck_name}
        </div>
        <div className="text-xs text-gray-500">
          This week · {sortedEvents.length}{" "}
          {sortedEvents.length === 1 ? "event" : "events"}
        </div>
      </div>

      {sortedEvents.length === 0 ? (
        <div className="p-8 text-center text-sm text-gray-500">
          No events scheduled this week. Check back soon!
        </div>
      ) : (
        <div className="flex flex-col md:flex-row min-h-[420px]">
          {/* Map */}
          <div className="md:w-3/5 h-[280px] md:h-[420px] relative">
            <MapContainer
              center={center}
              zoom={11}
              style={{ height: "100%", width: "100%" }}
              scrollWheelZoom={false}
              data-testid="embed-map"
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {sortedEvents.map((ev) => (
                <Marker
                  key={ev.id}
                  position={[ev.lat, ev.lng]}
                  icon={_pinIcon}
                >
                  <Popup>
                    <div className="font-semibold text-sm">
                      {ev.event_name || state.data.truck_name}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {_fmtDay(ev.starts_at)} · {_fmtTime(ev.starts_at)}–
                      {_fmtTime(ev.ends_at)}
                    </div>
                    {ev.address && (
                      <div className="text-xs text-gray-600 mt-1">
                        {ev.address}
                      </div>
                    )}
                    <a
                      href={_linkForEvent(ev)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block mt-2 text-xs font-semibold text-orange-600 hover:text-orange-700"
                    >
                      Open in TruckTrackr →
                    </a>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>

          {/* List */}
          <ul
            className="md:w-2/5 divide-y divide-gray-100 overflow-y-auto md:max-h-[420px]"
            data-testid="embed-event-list"
          >
            {sortedEvents.map((ev) => (
              <li key={ev.id}>
                <a
                  href={_linkForEvent(ev)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block px-4 py-3 hover:bg-orange-50 transition"
                  data-testid={`embed-event-${ev.id}`}
                >
                  <div className="text-xs font-bold uppercase tracking-wide text-orange-600">
                    {_fmtDay(ev.starts_at)}
                  </div>
                  <div className="mt-0.5 font-semibold text-sm text-gray-900 truncate">
                    {ev.event_name || "Truck stop"}
                  </div>
                  <div className="text-xs text-gray-600 mt-0.5">
                    {_fmtTime(ev.starts_at)} – {_fmtTime(ev.ends_at)}
                  </div>
                  {ev.address && (
                    <div className="text-xs text-gray-500 mt-0.5 truncate">
                      {ev.address}
                    </div>
                  )}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Frame>
  );
}

/**
 * Frame — minimal chrome (light theme, rounded card, "Powered by"
 * footer with utm-tagged backlink). The footer is mandatory: it's the
 * SEO leverage that justifies giving away this widget for free.
 */
function Frame({ children }) {
  return (
    <div
      className="min-h-screen bg-white text-gray-900 font-sans"
      data-testid="embed-frame"
    >
      <div className="max-w-3xl mx-auto bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        {children}
        <a
          href={`${TT_BASE}/?utm_source=embed&utm_medium=schedule_widget&utm_campaign=powered_by`}
          target="_blank"
          rel="noopener noreferrer"
          className="block px-4 py-2 text-[11px] text-gray-500 hover:text-orange-600 transition border-t border-gray-100 text-right"
          data-testid="embed-powered-by"
        >
          Powered by <span className="font-semibold">TruckTrackr</span> →
        </a>
      </div>
    </div>
  );
}
